import * as THREE from "three";

import type { AudioFeatures } from "../audio/types";
import type { AltSceneApi, CameraPersonality } from "./altScenes";
import type { BioclastParams, QualityTier } from "./scenes/bioclast";

export type LiveMathParticleMode =
  | "radialPulse"
  | "superform"
  | "spiralVortex"
  | "hopfFibration"
  | "fibonacciSequence";

const MODE_INDEX: Record<LiveMathParticleMode, number> = {
  radialPulse: 0,
  superform: 1,
  spiralVortex: 2,
  hopfFibration: 3,
  fibonacciSequence: 4,
};

const MAX_PARTICLES = 120_000;

type UniformMap = Record<string, THREE.IUniform<number>> & {
  uTime: THREE.IUniform<number>;
  uCount: THREE.IUniform<number>;
  uMode: THREE.IUniform<number>;
  uHue: THREE.IUniform<number>;
  uIntensity: THREE.IUniform<number>;
  uOpacity: THREE.IUniform<number>;
  uPointSize: THREE.IUniform<number>;
  uAmplitude: THREE.IUniform<number>;
  uFrequency: THREE.IUniform<number>;
  uRotation: THREE.IUniform<number>;
  uDepthScale: THREE.IUniform<number>;
  uWarmth: THREE.IUniform<number>;
  uBass: THREE.IUniform<number>;
  uMid: THREE.IUniform<number>;
  uTreble: THREE.IUniform<number>;
  uBeat: THREE.IUniform<number>;
  uFlow: THREE.IUniform<number>;
};

function hashUnit(value: number): number {
  const result = Math.sin(value) * 43758.5453123;
  return result - Math.floor(result);
}

function smoothBeat(prev: number, beat: number, dt: number): number {
  if (beat > prev) return beat;
  return prev * Math.exp(-dt * 5);
}

function hsl(h: number, s: number, l: number): THREE.Color {
  const color = new THREE.Color();
  color.setHSL(((h % 360) + 360) % 360 / 360, s, l);
  return color;
}

function qualityCap(tier: QualityTier): number {
  switch (tier) {
    case "mobile": return 30_000;
    case "standard": return 60_000;
    case "high": return 85_000;
    case "ultra": return 110_000;
    case "lab": return MAX_PARTICLES;
  }
}

const VERTEX_SHADER = `
  precision highp float;
  attribute float aIndex;
  attribute vec2 aSeed;
  uniform float uTime;
  uniform float uCount;
  uniform float uMode;
  uniform float uHue;
  uniform float uIntensity;
  uniform float uOpacity;
  uniform float uPointSize;
  uniform float uAmplitude;
  uniform float uFrequency;
  uniform float uRotation;
  uniform float uDepthScale;
  uniform float uWarmth;
  uniform float uBass;
  uniform float uMid;
  uniform float uTreble;
  uniform float uBeat;
  uniform float uFlow;
  varying vec3 vColor;
  varying float vAlpha;
  varying float vEnergy;

  const float TAU = 6.28318530718;
  const float PI = 3.14159265359;
  const float GOLDEN_ANGLE = 2.39996322973;

  vec3 hsl2rgb(vec3 c) {
    vec3 rgb = clamp(abs(mod(c.x * 6.0 + vec3(0.0, 4.0, 2.0), 6.0) - 3.0) - 1.0, 0.0, 1.0);
    return c.z + c.y * (rgb - 0.5) * (1.0 - abs(2.0 * c.z - 1.0));
  }

  vec3 referencePalette(float x, float energy, float warmthBias) {
    float warmth = clamp(uWarmth + warmthBias, 0.0, 1.0);
    vec3 cool = hsl2rgb(vec3(0.55, 0.45, 0.55));
    vec3 amber = hsl2rgb(vec3(0.10, 0.65, 0.55));
    vec3 hot = hsl2rgb(vec3(0.05, 0.78, 0.62));
    vec3 magenta = hsl2rgb(vec3(0.92, 0.70, 0.55));
    vec3 base = mix(cool, amber, smoothstep(0.18, 0.78, warmth));
    vec3 hi = mix(amber, hot, clamp(energy * 0.6, 0.0, 1.0));
    vec3 color = mix(base, hi, smoothstep(0.32, 0.92, x));
    return mix(color, magenta, smoothstep(0.55, 0.95, energy) * 0.18);
  }

  mat2 rot(float a) {
    float s = sin(a);
    float c = cos(a);
    return mat2(c, -s, s, c);
  }

  vec3 rotateY(vec3 p, float a) {
    p.xz = rot(a) * p.xz;
    return p;
  }

  vec3 rotateX(vec3 p, float a) {
    p.yz = rot(a) * p.yz;
    return p;
  }

  float superRadius(float a, float m, float n1, float n2, float n3) {
    float c = abs(cos(m * a * 0.25));
    float s = abs(sin(m * a * 0.25));
    return pow(max(0.0001, pow(c, n2) + pow(s, n3)), -1.0 / max(0.15, n1));
  }

  vec4 radialPulse(float t, vec2 seed) {
    float y0 = 1.0 - 2.0 * t;
    float r0 = sqrt(max(0.0, 1.0 - y0 * y0));
    float theta = aIndex * GOLDEN_ANGLE + uTime * (0.012 + uRotation * 0.05);

    float topMask = smoothstep(0.0, 1.0, y0);
    float bowlMask = 1.0 - smoothstep(-1.0, 0.0, y0);
    float coneRadius = mix(1.0, 0.04, topMask);
    float bowlRadius = mix(1.0, 1.05, bowlMask);
    float radialScale = coneRadius * bowlRadius;

    float stemPull = pow(topMask, 2.0) * 0.55;
    float yScale = mix(0.85, 0.85 + stemPull * 1.4, 1.0);

    float rd = 1.0 - r0;
    float ringPhase = rd * TAU * (uFrequency * 1.6) - uTime * (0.85 + uFlow * 0.4);
    float ring = sin(ringPhase + uBass * 1.2);

    float radius = 22.0;
    vec3 p;
    p.x = cos(theta) * r0 * radius * radialScale;
    p.z = sin(theta) * r0 * radius * radialScale;
    p.y = y0 * radius * yScale;

    p.xz *= 1.0 + ring * uAmplitude * 0.14 + uBeat * 0.06;
    p.y += ring * uAmplitude * 2.0 * (0.4 + bowlMask * 0.6);

    return vec4(p, topMask);
  }

  vec4 superform(float t, vec2 seed) {
    float yBase = 1.0 - 2.0 * t;
    float phi0 = acos(clamp(yBase, -0.98, 0.98));
    float adv = uTime * (0.20 + uFlow * 0.18 + uRotation * 0.12);
    float lane = seed.x * TAU;

    float phi = clamp(
      phi0 + 0.22 * sin(adv * 0.55 + lane + t * TAU),
      0.035,
      PI - 0.035
    );
    float theta = aIndex * GOLDEN_ANGLE
                + adv * (0.75 + seed.y * 0.45)
                + 0.22 * sin(phi * 3.0 - adv * 0.8 + lane);

    float m = 4.0 + uFrequency * 1.35 + 0.45 * sin(uTime * 0.13 + seed.y * TAU);
    float n1 = 0.92 + uAmplitude * 0.42 + 0.08 * sin(uTime * 0.17 + seed.x * TAU);
    float n2 = 1.45 + 0.25 * sin(uTime * 0.11 + seed.y * 5.0);
    float n3 = 1.45 + 0.25 * cos(uTime * 0.10 + seed.x * 4.0);

    float gAz = superRadius(theta, m, n1, n2, n3);
    float gLat = superRadius(phi - PI * 0.5, 2.0 + uFrequency * 0.18, n1, 1.6, 1.6);
    float shape = mix(1.0, gAz * gLat, clamp(uAmplitude * 0.38, 0.0, 0.48));

    float stream = sin(theta * 2.0 + phi * 5.0 - uTime * (1.10 + uFlow * 0.65) + lane);
    float fine = sin(theta * 7.0 - phi * 2.0 - uTime * 1.7 + seed.y * TAU);

    float r0 = sin(phi);
    float y0 = cos(phi);
    float radius = 21.5 * shape;
    vec3 p = vec3(cos(theta) * r0, y0, sin(theta) * r0) * radius;

    vec3 eTheta = normalize(vec3(-sin(theta), 0.0, cos(theta)));
    vec3 ePhi = normalize(vec3(cos(theta) * cos(phi), -sin(phi), sin(theta) * cos(phi)));
    p += eTheta * stream * uAmplitude * 0.85;
    p += ePhi * fine * uAmplitude * 0.35;
    p *= 1.0 + uBeat * 0.035;

    float band = 0.5 + 0.5 * stream;
    return vec4(p, band);
  }

  vec4 spiralVortex(float t, vec2 seed) {
    float bucket = mod(aIndex, 8.0);
    float subtype = step(7.0, bucket);

    if (subtype > 0.5) {
      float innerCount = max(1.0, floor(uCount / 8.0));
      float innerIdx = floor(aIndex / 8.0);
      float u = (innerIdx / innerCount) * TAU + uTime * (0.05 + uRotation * 0.08);
      float v = seed.y * TAU;
      float majorRadius = 8.0;
      float minorRadius = 1.3 + uAmplitude * 0.3 + uBeat * 0.20;
      vec3 p;
      p.x = (majorRadius + minorRadius * cos(v)) * cos(u);
      p.z = (majorRadius + minorRadius * cos(v)) * sin(u);
      p.y = minorRadius * sin(v) * 0.45;
      return vec4(p, 1.0);
    } else {
      float cols = 280.0;
      float outerCount = uCount * 0.875;
      float rowsF = max(2.0, floor(outerCount / cols));
      float col = mod(aIndex, cols);
      float row = floor(aIndex / cols);
      float u = (col + seed.x * 0.5) / cols * TAU + uTime * (0.04 + uRotation * 0.06);
      float v = (row + seed.y * 0.5) / rowsF * TAU;

      float majorRadius = 14.0;
      float minorRadius = 7.5 + uAmplitude * 0.6;

      float lift = sin(u * 3.0 + uTime * 0.4) * (0.6 + uAmplitude * 0.5);
      float twist = sin(v) * (0.4 + uFrequency * 0.15);

      vec3 p;
      p.x = (majorRadius + minorRadius * cos(v)) * cos(u + twist * 0.04);
      p.z = (majorRadius + minorRadius * cos(v)) * sin(u + twist * 0.04);
      p.y = minorRadius * sin(v) + lift;

      return vec4(p, 0.0);
    }
  }

  vec4 hopfFibration(float t, vec2 seed) {
    float fiberCount = 64.0;
    float fiberLen = max(96.0, floor(uCount / fiberCount));
    float fiberIdx = mod(floor(aIndex / fiberLen), fiberCount);
    float along = mod(aIndex, fiberLen);
    float s = (along + 0.5) / fiberLen;

    float latBands = 8.0;
    float lonBands = max(3.0, floor(fiberCount / latBands));
    float latIdx = mod(fiberIdx, latBands);
    float lonIdx = floor(fiberIdx / latBands);
    float latT = (latIdx + 0.5) / latBands;
    float lonT = (lonIdx + 0.5) / lonBands;

    float c0 = sin(mix(-1.05, 1.02, latT)) * 0.78;
    float c = clamp(c0 + 0.028 * sin(uTime * 0.10 + lonIdx * 0.37), -0.84, 0.80);
    float bphi = lonT * TAU
               + latIdx * GOLDEN_ANGLE * 0.23
               + uTime * (0.030 + uFlow * 0.014)
               + 0.045 * sin(uTime * 0.08 + latIdx * 0.9);
    float bs = sqrt(max(0.0, 1.0 - c * c));
    float a = bs * cos(bphi);
    float b = bs * sin(bphi);

    float xi = atan(b, a);

    float streamSpeed = 0.55 + uFlow * 0.34 + uRotation * 0.20;
    float ang = s * TAU
              + uTime * streamSpeed
              + latIdx * 0.19
              + lonIdx * 0.047
              + 0.06 * sin(uTime * 0.14 + lonIdx * 0.31);

    float c1 = sqrt(0.5 * (1.0 + c));
    float c2 = sqrt(0.5 * (1.0 - c));
    float a1 = (ang + xi) * 0.5;
    float a2 = (ang - xi) * 0.5;

    float x4 = c1 * cos(a1);
    float y4 = c1 * sin(a1);
    float z4 = c2 * cos(a2);
    float w4 = c2 * sin(a2);

    float rawDenom = 1.0 - w4;
    float visibility = smoothstep(0.36, 0.86, rawDenom);
    float denom = max(rawDenom, 0.30);
    vec3 p = vec3(x4, y4, z4) * (18.0 / denom);

    float mag = length(p);
    float cap = 58.0;
    if (mag > cap) p *= cap / mag;

    p = rotateX(p, 0.34);
    p = rotateY(p, -0.28);
    p *= 1.0 + uBeat * 0.025;

    float stripe = pow(0.5 + 0.5 * sin(s * TAU * 5.0 - uTime * (2.4 + uFlow * 0.9) + lonIdx * 0.41), 5.0);
    float family = 0.5 + 0.5 * sin(latIdx * 0.75 + lonIdx * 0.23);
    float subtype = visibility * (0.34 + 0.18 * family + 0.34 * stripe);
    return vec4(p, subtype);
  }

  vec4 fibonacciSequence(float t, vec2 seed) {
    float armCount = 34.0;
    float armIdx = mod(aIndex, armCount);
    float stepIdx = floor(aIndex / armCount);
    float steps = max(1.0, floor(uCount / armCount));
    float drift = uTime * (8.0 + uFlow * 18.0 + uRotation * 8.0);
    float q = fract((stepIdx + drift + armIdx * 0.21) / steps);
    float theta = armIdx * GOLDEN_ANGLE
                + q * TAU * (2.35 + uFrequency * 0.42)
                + q * q * (1.15 + uDepthScale * 0.55);

    float sqrtQ = sqrt(q);
    float radius = 1.8 + 30.0 * sqrtQ;
    float armWave = sin(armIdx * 0.62 + q * TAU * (5.0 + uFrequency * 0.8) - uTime * (0.8 + uFlow * 0.5));
    float shell = log(1.0 + q * 18.0);

    vec3 p;
    p.x = cos(theta) * radius * (1.0 + armWave * uAmplitude * 0.035);
    p.z = sin(theta) * radius * (1.0 + armWave * uAmplitude * 0.035);
    p.y = (q - 0.5) * (3.8 + uDepthScale * 6.8)
        + sin(theta * 0.13 - uTime * 1.2) * (0.7 + uAmplitude * 0.9);

    float curl = smoothstep(0.16, 1.0, q);
    p.yz = rot((curl - 0.5) * (0.42 + uDepthScale * 0.36)) * p.yz;
    p.xz = rot(shell * 0.26 + uTime * (0.035 + uRotation * 0.08)) * p.xz;

    float rib = pow(0.5 + 0.5 * sin(q * TAU * 13.0 + armIdx * GOLDEN_ANGLE - uTime * (1.8 + uFlow)), 3.2);
    rib = max(rib, pow(0.5 + 0.5 * sin(q * TAU * 21.0 - armIdx * 0.74 + uTime * 1.1), 5.0) * 0.72);
    p *= 1.0 + uBeat * 0.035 + rib * uAmplitude * 0.018;

    return vec4(p, rib);
  }

  vec4 sampleMathMode(float mode, float t, vec2 seed) {
    float m = mod(mode, 5.0);
    if (m < 0.5) {
      return radialPulse(t, seed);
    } else if (m < 1.5) {
      return superform(t, seed);
    } else if (m < 2.5) {
      return spiralVortex(t, seed);
    } else if (m < 3.5) {
      return hopfFibration(t, seed);
    }
    return fibonacciSequence(t, seed);
  }

  void main() {
    float count = max(1.0, uCount);
    float t = clamp((aIndex + 0.5) / count, 0.0, 1.0);

    vec4 packed = sampleMathMode(uMode, t, aSeed);
    vec3 p = packed.xyz;
    float subtype = packed.w;

    float preEnergy = clamp((uBass + uMid + uTreble) / 3.0 + uBeat * 0.6, 0.0, 1.5);

    vec3 n = normalize(p + vec3(0.0001));
    float micro = sin(uTime * 1.1 + aSeed.x * 41.0 + t * 23.0) * 0.18
                + sin(uTime * 0.7 + aSeed.y * 17.0 + length(p) * 0.08) * 0.12;
    p += n * micro * uAmplitude * (0.9 + preEnergy * 0.6);

    p = rotateY(p, uTime * (0.030 + uRotation * 0.13));
    p = rotateX(p, sin(uTime * 0.16) * 0.08 * uDepthScale);

    vec4 mvPosition = modelViewMatrix * vec4(p, 1.0);

    float sizePulse = smoothstep(0.45, 0.95, preEnergy) * 0.20 + uBeat * 0.25;
    float distScale = mix(1.0, 150.0 / max(46.0, -mvPosition.z), 0.36);
    float modePointScale = 1.0;
    if (uMode >= 2.5 && uMode < 3.5) modePointScale = 0.72;
    else if (uMode >= 3.5) modePointScale = 0.48;
    gl_PointSize = uPointSize * (0.92 + sizePulse * 0.48) * distScale * modePointScale;
    gl_Position = projectionMatrix * mvPosition;

    vec3 normal = normalize(p + vec3(0.0001));
    vec3 lightDir = normalize(vec3(0.55, 0.62, 0.55));
    float light = 0.55 + max(0.0, dot(normal, lightDir)) * 0.55;
    float rim = pow(1.0 - abs(dot(normalize(-mvPosition.xyz), normal)), 2.2);
    float grad = clamp(0.45 + p.y * 0.012 + length(p.xz) * 0.011, 0.0, 1.0);

    float warmthBias = 0.0;
    float modeVis = 1.0;
    if (uMode < 0.5) {
      warmthBias = 0.05 + subtype * 0.15;
      grad += subtype * 0.25;
    } else if (uMode < 1.5) {
      warmthBias = 0.08;
    } else if (uMode < 2.5) {
      warmthBias = subtype > 0.5 ? 0.30 : -0.45;
      grad = subtype > 0.5 ? 0.65 : 0.30;
    } else if (uMode < 3.5) {
      modeVis = clamp(0.20 + subtype * 0.95, 0.0, 1.0);
      warmthBias = 0.12 + subtype * 0.12;
      grad = 0.34 + subtype * 0.38 + aSeed.x * 0.10;
      rim *= 0.70 + 0.30 * modeVis;
      light *= 0.72 + 0.34 * modeVis;
    } else {
      modeVis = 0.28 + subtype * 0.68;
      warmthBias = 0.22 + subtype * 0.16;
      grad = 0.32 + subtype * 0.55 + aSeed.y * 0.08;
      rim *= 0.85 + subtype * 0.25;
    }

    vec3 pal = referencePalette(clamp(grad, 0.0, 1.0), preEnergy, warmthBias);
    vec3 hueTint = hsl2rgb(vec3(fract(uHue + 0.0), 0.45, 0.55));
    pal = mix(pal, hueTint, 0.05);

    float modeBrightness = 1.82;
    if (uMode >= 0.5 && uMode < 1.5) modeBrightness = 1.72;
    else if (uMode >= 2.5 && uMode < 3.5) modeBrightness = 2.35;
    else if (uMode >= 3.5) modeBrightness = 2.28;
    vColor = pal * (1.68 + light * 0.76 + rim * 0.68 + preEnergy * (0.3 + uIntensity * 0.12)) * modeBrightness;
    float modeAlphaScale = 1.0;
    if (uMode >= 2.5 && uMode < 3.5) modeAlphaScale = 1.16;
    else if (uMode >= 3.5) modeAlphaScale = 1.12;
    float alphaBase = 0.38 + uIntensity * 0.026 + preEnergy * 0.055;
    if (uMode >= 2.5 && uMode < 3.5) alphaBase = 0.5 + uIntensity * 0.026 + preEnergy * 0.065;
    else if (uMode >= 3.5) alphaBase = 0.46 + uIntensity * 0.026 + preEnergy * 0.065;
    vAlpha = alphaBase * modeVis * modeAlphaScale * clamp(uOpacity, 0.0, 1.0);
    vEnergy = preEnergy;
  }
`;

const FRAGMENT_SHADER = `
  precision highp float;
  varying vec3 vColor;
  varying float vAlpha;
  varying float vEnergy;

  void main() {
    vec2 uv = gl_PointCoord - 0.5;
    float d = length(uv);
    if (d > 0.5) discard;

    float core = smoothstep(0.5, 0.0, d);
    core = pow(core, 2.45);
    float halo = exp(-d * d * 14.0) * (0.08 + vEnergy * 0.075);

    float alpha = clamp((core + halo) * vAlpha * 1.04, 0.0, 1.0);
    if (alpha < 0.01) discard;

    vec3 col = vColor * (1.5 + core * 1.22 + halo * 0.82 + vEnergy * 0.3);
    gl_FragColor = vec4(col, alpha);
  }
`;

export class LiveMathParticleScene implements AltSceneApi {
  scene = new THREE.Scene();
  camera: THREE.PerspectiveCamera;
  readonly backdrop = { r: 3, g: 4, b: 13, alpha: 0.48, blurPx: 58 };
  readonly cameraPersonality: CameraPersonality = "orbit";
  beatLevel = 0;

  private readonly geometry = new THREE.BufferGeometry();
  private readonly material: THREE.ShaderMaterial;
  private readonly points: THREE.Points;
  private readonly uniforms: UniformMap = {
    uTime: { value: 0 },
    uCount: { value: 40_000 },
    uMode: { value: 0 },
    uHue: { value: 0 },
    uIntensity: { value: 1 },
    uOpacity: { value: 0.8 },
    uPointSize: { value: 1 },
    uAmplitude: { value: 0.3 },
    uFrequency: { value: 2 },
    uRotation: { value: 0.55 },
    uDepthScale: { value: 0.4 },
    uWarmth: { value: 0.62 },
    uBass: { value: 0 },
    uMid: { value: 0 },
    uTreble: { value: 0 },
    uBeat: { value: 0 },
    uFlow: { value: 1 },
  };
  private time = 0;

  constructor(width: number, height: number, mode: LiveMathParticleMode) {
    this.camera = new THREE.PerspectiveCamera(48, width / Math.max(1, height), 0.1, 600);
    if (mode === "spiralVortex") {
      this.camera.position.set(0, 28, 55);
    } else if (mode === "fibonacciSequence") {
      this.camera.position.set(0, 34, 74);
    } else {
      this.camera.position.set(0, 18, 72);
    }
    this.camera.lookAt(0, 0, 0);

    const positions = new Float32Array(MAX_PARTICLES * 3);
    const indices = new Float32Array(MAX_PARTICLES);
    const seeds = new Float32Array(MAX_PARTICLES * 2);
    for (let index = 0; index < MAX_PARTICLES; index++) {
      indices[index] = index;
      seeds[index * 2] = hashUnit(index + 13);
      seeds[index * 2 + 1] = hashUnit(index + 29);
    }

    this.geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    this.geometry.setAttribute("aIndex", new THREE.BufferAttribute(indices, 1));
    this.geometry.setAttribute("aSeed", new THREE.BufferAttribute(seeds, 2));
    this.geometry.setDrawRange(0, this.uniforms.uCount.value);
    this.uniforms.uMode.value = MODE_INDEX[mode];

    this.material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.points = new THREE.Points(this.geometry, this.material);
    this.points.frustumCulled = false;
    this.scene.add(this.points);
    this.setSize(width, height);
  }

  setSize(width: number, height: number): void {
    this.camera.aspect = width / Math.max(1, height);
    this.camera.updateProjectionMatrix();
  }

  update(dt: number, params: BioclastParams, features: AudioFeatures): void {
    this.beatLevel = smoothBeat(this.beatLevel, features.beat, dt);
    const level = Math.max(features.level, (features.bass + features.mid + features.treble) / 3);
    this.time += dt * (0.12 + params.flowSpeed * 0.54 + level * 0.42);

    const activeCount = Math.max(
      4_000,
      Math.min(MAX_PARTICLES, qualityCap(params.qualityTier), Math.round(params.mathParticleCount)),
    );
    this.geometry.setDrawRange(0, activeCount);

    this.uniforms.uTime.value = this.time;
    this.uniforms.uCount.value = activeCount;
    this.uniforms.uHue.value = ((params.paletteHue % 360) + 360) / 360;
    this.uniforms.uIntensity.value = Math.max(0.8, params.intensity);
    this.uniforms.uOpacity.value = THREE.MathUtils.clamp(params.opacity, 0, 1);
    this.uniforms.uPointSize.value = Math.max(0.25, params.mathPointSize);
    this.uniforms.uAmplitude.value = Math.max(0, params.mathAmplitude);
    this.uniforms.uFrequency.value = Math.max(0.2, params.mathFrequency);
    this.uniforms.uRotation.value = Math.max(0, params.mathRotationSpeed);
    this.uniforms.uDepthScale.value = THREE.MathUtils.clamp(params.mathDepthScale, 0, 1.4);
    this.uniforms.uWarmth.value = THREE.MathUtils.clamp(params.mathWarmth, 0, 1);
    this.uniforms.uBass.value = features.bass * params.bassGravity;
    this.uniforms.uMid.value = features.mid * params.midCurl;
    this.uniforms.uTreble.value = features.treble * params.trebleShimmer;
    this.uniforms.uBeat.value = this.beatLevel;
    this.uniforms.uFlow.value = params.flowSpeed;

    const backdropColor = hsl(params.paletteHue + params.mathWarmth * 80, params.saturation, 0.11 + this.beatLevel * 0.08);
    this.backdrop.r = Math.round(backdropColor.r * 70 + features.bass * 25);
    this.backdrop.g = Math.round(backdropColor.g * 58 + features.mid * 35);
    this.backdrop.b = Math.round(backdropColor.b * 90 + features.treble * 35);
    this.backdrop.alpha = 0.42 + level * 0.22;
    this.backdrop.blurPx = 48 + level * 28;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
    this.scene.clear();
  }
}
